/*
 * Lazy-Video-Steuerung für den Hero-Slider (Slideshow v2).
 * - Start beim window-"load"-Event, spätestens aber START_FALLBACK_MS nach
 *   DOMContentLoaded (mobil kann "load" durch Drittskripte sehr spät kommen)
 * - Nur das Video des aktiven Slides bekommt ein src und spielt
 * - Mobil (< 750px) wird die 720p-Variante geladen (data-src-mobile)
 * - Nicht aktive Slides werden pausiert und zurückgesetzt
 * - Bei Data-Saver oder reduced motion: gar kein Video (nur Poster)
 * - Blockiert das Gerät Autoplay (z. B. iOS-Stromsparmodus) oder lädt das
 *   Video nicht, wird es als fehlgeschlagen markiert (data-video-failed) und
 *   das Event "hero-video:failed" gefeuert -> theme.js schaltet auf Bild-Timer.
 *
 * Erkennt das aktive Slide über die Klasse .swiper-slide-active,
 * ist also unabhängig davon, wann/wie der Swiper initialisiert wird.
 */
(() => {
  const MOBILE = window.matchMedia('(max-width: 749px)');
  const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');
  const START_FALLBACK_MS = 2500;

  // Muss dieselben Bedingungen haben wie SwiperConfigurator.videoAllowed() in theme.js
  const videoAllowed = () => {
    const conn = navigator.connection;
    return !REDUCED_MOTION.matches && !(conn && conn.saveData);
  };

  const markFailed = (video) => {
    if (video.dataset.videoFailed) return;
    video.dataset.videoFailed = 'true';
    video.dispatchEvent(new CustomEvent('hero-video:failed'));
  };

  function init(root) {
    if (root.dataset.lazyVideoInit) return;
    root.dataset.lazyVideoInit = 'true';

    let started = document.readyState === 'complete';
    let activeSlide = null;

    const getActiveSlide = () =>
      root.querySelector('.swiper-slide-active') || root.querySelector('.swiper-slide');

    const playSlide = (slide) => {
      const video = slide && slide.querySelector('video[data-src]');
      if (!video || video.dataset.videoFailed) return;
      if (!videoAllowed()) {
        console.info('[Hero-Video] übersprungen: reduced motion oder Datensparmodus aktiv');
        return;
      }

      if (!video.getAttribute('src')) {
        video.addEventListener('playing', () => slide.classList.add('is-video-playing'), { once: true });
        video.addEventListener('error', () => markFailed(video), { once: true });
        video.muted = true; // iOS verlangt die Property, nicht nur das Attribut
        video.src = (MOBILE.matches && video.dataset.srcMobile) || video.dataset.src;
      }

      const promise = video.play();
      if (promise) {
        promise.catch((err) => {
          // AbortError = play() wurde durch pause() unterbrochen (Slidewechsel) -> kein Fehler
          if (err && err.name !== 'AbortError') {
            console.warn('[Hero-Video] play() blockiert:', err.name, err.message);
            markFailed(video);
          }
        });
      }
    };

    const sync = () => {
      const slide = getActiveSlide();
      if (slide === activeSlide) return;
      activeSlide = slide;

      root.querySelectorAll('video[data-src]').forEach((video) => {
        if (slide && slide.contains(video)) return;
        if (video.getAttribute('src')) {
          video.pause();
          video.currentTime = 0;
        }
      });

      if (started) playSlide(slide);
    };

    const start = () => {
      if (started) return;
      started = true;
      activeSlide = null;
      sync();
    };

    new MutationObserver(sync).observe(root, {
      subtree: true,
      attributes: true,
      attributeFilter: ['class'],
    });

    if (!started) {
      window.addEventListener('load', start, { once: true });
      const startFallback = () => setTimeout(start, START_FALLBACK_MS);
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', startFallback, { once: true });
      } else {
        startFallback();
      }
    }

    sync();
  }

  const initAll = (scope) =>
    scope.querySelectorAll('[data-video-slider]').forEach(init);

  initAll(document);
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();